import {
  AdminCourseScheduleItemSchema,
  type AdminCourseScheduleItemType,
} from '@duke-hq/shared-constants/course';
import {
  CourseItemStatusSchema,
  type CourseItemStatus,
} from '@duke-hq/svc-api-db/schemas/enums/CourseItemStatus.schema';
import { z } from 'zod';

type DefaultScheduleRow = {
  Semester: number;
  Week: number;
  durationWeeks: string;
  'Unit Code': string;
  Title: string;
  id: string;
  type: string;
  unitId: string;
};

type CourseItemForDefaultSchedule = {
  status: CourseItemStatus;
  unitOfCompetency: {
    id: string;
    code: string;
    title: string;
    learningResources: Array<{
      resource: {
        kind: string;
        deleted_at: Date | null;
        id: string;
        title: string;
      };
    }>;
    unitAssessment: { id: string } | null;
  } | null;
};

export function buildDefaultScheduleData(
  courseItems: CourseItemForDefaultSchedule[],
): DefaultScheduleRow[] {
  const schedule: DefaultScheduleRow[] = [];

  courseItems
    .filter(({ status }) => status !== CourseItemStatusSchema.enum.SUPERSEDED)
    .forEach(({ unitOfCompetency }) => {
      if (unitOfCompetency) {
        schedule.push({
          Semester: 1,
          Week: 1,
          // Units have no duration; conversations/assessments default to one week.
          durationWeeks: '',
          'Unit Code': unitOfCompetency.code,
          Title: unitOfCompetency.title,
          id: unitOfCompetency.id,
          type: 'UNIT',
          unitId: unitOfCompetency.id,
        });
      }
      if (unitOfCompetency && unitOfCompetency.learningResources) {
        unitOfCompetency.learningResources
          .filter(
            (resource) =>
              resource.resource.kind === 'CONVERSATION' && resource.resource.deleted_at === null,
          )
          .forEach(({ resource }) => {
            schedule.push({
              Semester: 1,
              Week: 1,
              durationWeeks: '1',
              'Unit Code': unitOfCompetency.code,
              Title: resource.title,
              id: resource.id,
              type: resource.kind,
              unitId: unitOfCompetency.id,
            });
          });
      }
      if (unitOfCompetency && unitOfCompetency.unitAssessment) {
        schedule.push({
          Semester: 1,
          Week: 1,
          durationWeeks: '1',
          'Unit Code': unitOfCompetency.code,
          Title: 'Assessment',
          id: unitOfCompetency.unitAssessment.id,
          type: 'ASSESSMENT',
          unitId: unitOfCompetency.id,
        });
      }
    });

  return schedule;
}

export function parseAdminCourseSchedule(rawSchedule: string): AdminCourseScheduleItemType[] {
  if (rawSchedule.trim() === '' || rawSchedule.trim() === '{}') {
    return [];
  }

  const result = z.array(AdminCourseScheduleItemSchema).safeParse(JSON.parse(rawSchedule));

  if (result.success) {
    return result.data;
  }

  console.error('Unable to parse admin course schedule', result.error);
  return [];
}

// Build CSV rows from parsed schedule items. A durationWeeks column is included for every row
// (empty for units) so it's never dropped when the first row lacks it.
export function toScheduleCsvRows(items: AdminCourseScheduleItemType[]) {
  return items.map((item) => ({
    Semester: item.Semester,
    Week: item.Week,
    durationWeeks: item.durationWeeks ?? '',
    'Unit Code': item['Unit Code'],
    Title: item.Title,
    id: item.id,
    type: item.type,
    unitId: item.unitId,
  }));
}

// Uploaded CSVs (header mode) turn empty cells into empty strings, so a unit's blank duration comes
// back as durationWeeks: '' — which fails the schema's positive-integer rule and blanks the whole
// schedule. Drop an empty durationWeeks so it's absent (a valid "no duration") rather than ''.
export function normaliseUploadedScheduleRows(
  rows: Array<Record<string, string>>,
): Array<Record<string, string>> {
  return rows.map((row) => {
    if (typeof row.durationWeeks !== 'string' || row.durationWeeks.trim() !== '') {
      return row;
    }
    const next = { ...row };
    delete next.durationWeeks;
    return next;
  });
}
